import { useState, useMemo, useCallback, useEffect } from 'react';
import { empIncentivesApi, PAYHEAD_OPTIONS } from '../api/empIncentivesApi';
import { appToast } from '../../../../components/AppToast';

export function useEmpIncentivesList() {
  const [datePresetFilter, setDatePresetFilter] = useState('All');
  const [fromDate, setFromDate] = useState('');
  const [toDate, setToDate] = useState('');
  const [payHeadFilter, setPayHeadFilter] = useState('All');
  const [searchField, setSearchField] = useState('Pay Head');
  const [searchText, setSearchText] = useState('');
  const [statusFilter, setStatusFilter] = useState('All');
  const [records, setRecords] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(25);

  const statusOptions = [
    { value: 'All', label: 'All Incentives' },
    { value: 'Approved', label: 'Approved' },
    { value: 'Pending', label: 'Pending' },
  ];

  const datePresetOptions = [
    { value: 'All', label: 'All Periods' },
    { value: 'Current Month', label: 'Current Month' },
    { value: 'Last Month', label: 'Last Month' },
    { value: 'This Quarter', label: 'This Quarter' },
    { value: 'This Year', label: 'This Year (2026)' },
  ];

  const searchFieldOptions = [
    { value: 'Pay Head', label: 'Pay Head' },
    { value: 'Voucher No', label: 'Voucher Number (Vno)' },
    { value: 'Remarks', label: 'Remarks' },
  ];

  const tools = [
    { key: 'pdf', label: 'Export Incentives Summary to PDF', icon: 'picture_as_pdf' },
    { key: 'excel', label: 'Export Incentives to Excel', icon: 'table_view' },
    { key: 'print', label: 'Print Incentives Statement', icon: 'print' },
  ];

  const loadData = useCallback(async () => {
    setIsLoading(true);
    try {
      const res = await empIncentivesApi.getList();
      setRecords(res.data || []);
    } catch {
      // Fallback
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const handleDatePresetChange = useCallback((preset) => {
    setDatePresetFilter(preset);
    if (preset === 'Current Month') {
      setFromDate('2026-08-01');
      setToDate('2026-08-31');
    } else if (preset === 'Last Month') {
      setFromDate('2026-07-01');
      setToDate('2026-07-31');
    } else if (preset === 'This Quarter') {
      setFromDate('2026-07-01');
      setToDate('2026-09-30');
    } else if (preset === 'This Year') {
      setFromDate('2026-01-01');
      setToDate('2026-12-31');
    } else {
      setFromDate('');
      setToDate('');
    }
  }, []);

  // Filtered records
  const filteredRecords = useMemo(() => {
    return records.filter((item) => {
      // Status filter
      if (statusFilter !== 'All' && item.status !== statusFilter) {
        return false;
      }

      // Pay Head filter
      if (payHeadFilter !== 'All' && item.payHead !== payHeadFilter) {
        return false;
      }

      // Date range filter
      if (fromDate && item.vDate < fromDate) {
        return false;
      }
      if (toDate && item.vDate > toDate) {
        return false;
      }

      // Text search
      if (searchText.trim()) {
        const query = searchText.trim().toLowerCase();
        if (searchField === 'Pay Head' && !item.payHead?.toLowerCase().includes(query)) return false;
        if (searchField === 'Voucher No' && !item.vno?.toLowerCase().includes(query)) return false;
        if (searchField === 'Remarks' && !item.remarks?.toLowerCase().includes(query)) return false;
      }

      return true;
    });
  }, [records, statusFilter, payHeadFilter, fromDate, toDate, searchText, searchField]);

  // Summary statistics
  const summary = useMemo(() => {
    let totalAmount = 0;
    let approvedAmount = 0;
    let pendingAmount = 0;
    const uniquePayheads = new Set();

    filteredRecords.forEach((r) => {
      const amt = Number(r.amount || 0);
      totalAmount += amt;
      if (r.status === 'Approved') approvedAmount += amt;
      if (r.status === 'Pending') pendingAmount += amt;
      if (r.payHead) uniquePayheads.add(r.payHead);
    });

    const averageAmount = filteredRecords.length > 0 ? totalAmount / filteredRecords.length : 0;

    return {
      totalAmount,
      approvedAmount,
      pendingAmount,
      averageAmount,
      totalCount: filteredRecords.length,
      payheadsCount: uniquePayheads.size,
    };
  }, [filteredRecords]);

  // Pagination
  const totalCount = filteredRecords.length;
  const totalPages = Math.max(1, Math.ceil(totalCount / pageSize));
  const paginatedRecords = useMemo(() => {
    const start = (page - 1) * pageSize;
    return filteredRecords.slice(start, start + pageSize);
  }, [filteredRecords, page, pageSize]);

  // Actions
  const handleSearch = useCallback(() => {
    setPage(1);
    appToast.success('Employee incentives filtered');
  }, []);

  const resetFilters = useCallback(() => {
    setDatePresetFilter('All');
    setFromDate('');
    setToDate('');
    setPayHeadFilter('All');
    setStatusFilter('All');
    setSearchText('');
    setSearchField('Pay Head');
    setPage(1);
  }, []);

  const handleToolSelect = useCallback((toolKey) => {
    const key = String(toolKey || '').toLowerCase();
    if (key.includes('print')) {
      window.print();
    } else if (key.includes('pdf')) {
      appToast.success('Exporting Incentives Statement to PDF...');
    } else if (key.includes('excel')) {
      appToast.success('Exporting Incentives to Excel...');
    }
  }, []);

  return {
    state: {
      records: paginatedRecords,
      allFilteredRecords: filteredRecords,
      totalCount,
      page,
      pageSize,
      totalPages,
      datePresetFilter,
      fromDate,
      toDate,
      payHeadFilter,
      statusFilter,
      searchField,
      searchText,
      isLoading,
      datePresetOptions,
      payHeadOptions: PAYHEAD_OPTIONS,
      statusOptions,
      searchFieldOptions,
      tools,
      summary,
    },
    actions: {
      setDatePresetFilter: handleDatePresetChange,
      setFromDate,
      setToDate,
      setPayHeadFilter,
      setStatusFilter,
      setSearchField,
      setSearchText,
      setPage,
      setPageSize,
      handleSearch,
      resetFilters,
      handleToolSelect,
    },
  };
}
